import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  formatUsDateFromIso,
  parseUsDateToIso,
  normalizeUsDateTyping,
} from '../utils/usDateFormat';

/**
 * MM/DD/YYYY text field + calendar icon.
 * The native date input overlays the icon so taps open the picker on iOS
 * (showPicker / programmatic click on a hidden date input does not work there).
 */
const UsDatePicker = forwardRef(function UsDatePicker(
  {
    id,
    label,
    value,
    onChange,
    min,
    max,
    required,
    onBelowMin,
    onAboveMax,
    hint,
    labelClassName,
    inputClassName,
  },
  ref
) {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    setDisplay(value ? formatUsDateFromIso(value) : '');
  }, [value]);

  const commitFromDisplay = useCallback(() => {
    const trimmed = display.trim();
    if (!trimmed) {
      onChange('');
      return '';
    }
    const iso = parseUsDateToIso(trimmed);
    if (!iso) {
      onChange('');
      return '';
    }
    if (min && iso < min) {
      onChange('');
      setDisplay('');
      onBelowMin?.();
      return '';
    }
    if (max && iso > max) {
      onChange('');
      setDisplay('');
      onAboveMax?.();
      return '';
    }
    onChange(iso);
    setDisplay(formatUsDateFromIso(iso));
    return iso;
  }, [display, max, min, onAboveMax, onBelowMin, onChange]);

  useImperativeHandle(ref, () => ({ commit: commitFromDisplay }), [commitFromDisplay]);

  const handleNativeDateChange = (e) => {
    const v = e.target.value;
    // iOS Clear sets value to ""; Android Clear does the same — must not ignore empty.
    if (!v) {
      onChange('');
      setDisplay('');
      return;
    }
    if (min && v < min) return;
    if (max && v > max) return;
    onChange(v);
    setDisplay(formatUsDateFromIso(v));
  };

  const handleBlur = () => {
    commitFromDisplay();
  };

  const defaultLabelClass = 'block text-sm font-medium text-gray-700 mb-2';
  const defaultInputClass =
    'w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#674936] focus:border-transparent';

  return (
    <div>
      {label ? (
        <label className={labelClassName || defaultLabelClass} htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          placeholder="mm/dd/yyyy"
          autoComplete="off"
          value={display}
          onChange={(e) => setDisplay(normalizeUsDateTyping(e.target.value))}
          onBlur={handleBlur}
          className={inputClassName || defaultInputClass}
          maxLength={10}
          required={required}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9">
          <span
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md text-gray-500"
            aria-hidden="true"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </span>
          <input
            type="date"
            value={value || ''}
            min={min || undefined}
            max={max || undefined}
            onChange={handleNativeDateChange}
            aria-label="Open calendar to pick date"
            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          />
        </div>
      </div>
      {hint ? <p className="text-sm text-gray-500 mt-1">{hint}</p> : null}
    </div>
  );
});

export default UsDatePicker;
